import { Ban, CreditCard, Eye, ReceiptText, UserRound } from 'lucide-react';

import { formatCurrency } from '../../../../utils/formatCurrency';

import { PendingCashierSaleCardDTO } from '../../../../types/sale/dto/PendingCashierSaleCardDTO';

interface Props {
  sale: PendingCashierSaleCardDTO;

  onViewSale: (saleId: number) => void;

  onProcessSale: (sale: PendingCashierSaleCardDTO) => void;

  canCancel?: boolean;

  onCancelSale?: (sale: PendingCashierSaleCardDTO) => void;
}

const CashPendingSaleListItem = ({
  sale,
  onViewSale,
  onProcessSale,
  canCancel = false,
  onCancelSale,
}: Props) => {
  return (
    <div
      className="
        flex flex-col gap-4

        rounded-2xl border border-slate-200 bg-white p-4

        transition-all hover:border-primary/30 hover:shadow-sm

        dark:border-strokedark dark:bg-boxdark

        lg:flex-row lg:items-center lg:justify-between
      "
    >
      {/* ===================================================== */}
      {/* 🔹 Información */}
      {/* ===================================================== */}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <ReceiptText className="h-4 w-4 shrink-0 text-primary" />
          <h3 className="truncate text-base font-semibold text-slate-800 dark:text-white">
            {sale.documentNumber}
          </h3>

          <span
            className="
              shrink-0 rounded-full bg-warning/10 px-2 py-0.5

              text-[11px] font-semibold uppercase tracking-wide text-warning
            "
          >
            {sale.status}
          </span>
        </div>

        <div className="mt-2 flex items-center gap-2 text-sm">
          <UserRound className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="truncate font-semibold text-slate-800 dark:text-white">
            {sale.customerName}
          </span>
          <span className="text-slate-300">•</span>
          <span className="truncate text-xs text-slate-400">
            {sale.sellerName || 'Sin vendedor'}
          </span>
        </div>

        <p className="mt-0.5 text-xs text-slate-400">
          {sale.documentType || 'Documento'}
        </p>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Total + Acciones */}
      {/* ===================================================== */}

      <div className="flex items-center justify-between gap-4 lg:justify-end">
        <div className="text-left lg:text-right">
          <p className="text-xs uppercase tracking-wide text-slate-400">
            Total a cobrar
          </p>
          <p className="text-2xl font-extrabold text-primary">
            ${formatCurrency(sale.total)}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onViewSale(sale.saleId)}
            className="
              inline-flex items-center gap-2

              rounded-xl border border-slate-200 px-3 py-2

              text-sm font-medium text-slate-600 transition-all

              hover:border-primary hover:text-primary

              dark:border-strokedark dark:text-slate-300
            "
          >
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Ver</span>
          </button>

          {canCancel && onCancelSale && (
            <button
              onClick={() => onCancelSale(sale)}
              title="Anular venta pendiente de cobro"
              className="
                inline-flex items-center gap-2

                rounded-xl border border-danger/30 px-3 py-2

                text-sm font-medium text-danger transition-all

                hover:bg-danger/10
              "
            >
              <Ban className="h-4 w-4" />
              <span className="hidden sm:inline">Anular</span>
            </button>
          )}

          <button
            onClick={() => onProcessSale(sale)}
            className="
              inline-flex items-center gap-2

              rounded-xl bg-primary px-5 py-2.5

              text-sm font-semibold text-white transition-all

              hover:bg-opacity-90
            "
          >
            <CreditCard className="h-4 w-4" />
            Cobrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashPendingSaleListItem;
